import { useSearchParams } from "react-router-dom";
import { trpc } from "../../lib/api/trpc";

type QueryStats = {
  queriesByDay: Array<{ date: string; count: number }>;
  confidenceBreakdown: {
    high: number;
    medium: number;
    low: number;
    insufficient: number;
  };
  sourceBreakdown: {
    TEAMS_BOT: number;
    ADMIN_CHAT: number;
    API: number;
  };
  totalQueriesInRange: number;
  totalQueriesLast30Days: number;
  totalQueriesLast7Days: number;
  insufficientLast30Days: number;
  rangeDays: number;
};

function StatCard({
  label,
  value,
  numberClass,
  dotClass,
}: {
  label: string;
  value: number;
  numberClass: string;
  dotClass?: string;
}) {
  return (
    <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]">
      {dotClass ? (
        <div className="flex items-center gap-2 mb-[10px]">
          <span
            className={`inline-block w-[7px] h-[7px] rounded-full ${dotClass}`}
          />
          <span className="text-[13px] text-[var(--text-3)]">{label}</span>
        </div>
      ) : (
        <div className="text-[13px] text-[var(--text-3)] mb-[10px]">
          {label}
        </div>
      )}
      <div
        className={`font-serif text-[36px] font-normal leading-none ${numberClass}`}
      >
        {value}
      </div>
    </div>
  );
}

function BarChart({ data }: { data: Array<{ date: string; count: number }> }) {
  if (data.length === 0) return null;

  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const chartHeight = 160;
  const labelStep = Math.max(1, Math.ceil(data.length / 8));

  return (
    <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-5">
      <h3 className="text-[13px] font-semibold text-[var(--text)] mb-4">
        Queries per Day
      </h3>
      <div
        className="flex items-end gap-[2px]"
        style={{ height: chartHeight + 28 }}
      >
        {data.map((d, i) => {
          const barHeight = Math.max(2, (d.count / maxCount) * chartHeight);
          const showLabel = i % labelStep === 0 || i === data.length - 1;
          return (
            <div
              key={d.date}
              className="flex-1 flex flex-col items-center justify-end min-w-0"
              style={{ height: chartHeight + 28 }}
            >
              <div
                className="w-full max-w-[20px] rounded-t bg-[var(--accent)] transition-all"
                style={{ height: barHeight }}
                title={`${d.date}: ${d.count} queries`}
              />
              {showLabel && (
                <span className="text-[9px] text-[var(--text-faint)] mt-1 whitespace-nowrap">
                  {new Date(d.date + "T00:00:00").toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              )}
              {!showLabel && <span className="h-3 mt-1" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConfidenceBar({
  breakdown,
}: {
  breakdown: QueryStats["confidenceBreakdown"];
}) {
  const total =
    breakdown.high + breakdown.medium + breakdown.low + breakdown.insufficient;
  if (total === 0) return null;

  const segments = [
    {
      key: "high",
      label: "High",
      value: breakdown.high,
      color: "bg-[var(--ok-dot)]",
    },
    {
      key: "medium",
      label: "Medium",
      value: breakdown.medium,
      color: "bg-[var(--accent)]",
    },
    {
      key: "low",
      label: "Low",
      value: breakdown.low,
      color: "bg-[var(--warn-dot)]",
    },
    {
      key: "insufficient",
      label: "Insufficient",
      value: breakdown.insufficient,
      color: "bg-[var(--err-dot)]",
    },
  ];

  return (
    <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-5">
      <h3 className="text-[13px] font-semibold text-[var(--text)] mb-4">
        Confidence Breakdown
      </h3>
      {/* Stacked bar */}
      <div className="flex h-6 rounded-full overflow-hidden mb-4">
        {segments.map(
          (s) =>
            s.value > 0 && (
              <div
                key={s.key}
                className={`${s.color}`}
                style={{ width: `${(s.value / total) * 100}%` }}
              />
            ),
        )}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        {segments.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-sm">
            <span className={`inline-block w-3 h-3 rounded-sm ${s.color}`} />
            <span className="text-[var(--text-2)]">
              {s.label}: {s.value} (
              {total > 0 ? Math.round((s.value / total) * 100) : 0}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SourceBreakdown({
  breakdown,
}: {
  breakdown: QueryStats["sourceBreakdown"];
}) {
  const total = breakdown.TEAMS_BOT + breakdown.ADMIN_CHAT + breakdown.API;

  const items = [
    { label: "Teams Bot", value: breakdown.TEAMS_BOT },
    { label: "Admin Chat", value: breakdown.ADMIN_CHAT },
    { label: "API", value: breakdown.API },
  ];

  return (
    <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-5">
      <h3 className="text-[13px] font-semibold text-[var(--text)] mb-4">
        Source Breakdown
      </h3>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between">
            <span className="text-sm text-[var(--text-2)]">{item.label}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[var(--text)]">
                {item.value}
              </span>
              <span className="text-xs text-[var(--text-faint)] w-10 text-right">
                {total > 0 ? Math.round((item.value / total) * 100) : 0}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-[18px] animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]"
          >
            <div className="h-4 w-20 bg-[var(--surface-2)] rounded mb-3" />
            <div className="h-8 w-16 bg-[var(--surface-2)] rounded" />
          </div>
        ))}
      </div>
      <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-5">
        <div className="h-48 bg-[var(--surface-2)] rounded" />
      </div>
    </div>
  );
}

export function UsagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const range = (searchParams.get("range") || "30") as "7" | "30" | "90";

  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data, isLoading, isError } = trpc.analytics.getQueryStats.useQuery({
    range,
  });

  const stats = data as QueryStats | undefined;

  const handleRangeChange = (newRange: "7" | "30" | "90") => {
    setSearchParams({ range: newRange });
  };

  if (isLoading) return <Skeleton />;

  if (isError) {
    return (
      <div className="bg-[var(--err-bg)] border border-[var(--err-bg)] rounded-[12px] p-4">
        <p className="text-sm text-[var(--err-text)]">
          Failed to load analytics data.
        </p>
      </div>
    );
  }

  const totalQueries =
    stats?.totalQueriesInRange ?? stats?.totalQueriesLast30Days ?? 0;

  if (!stats || totalQueries === 0) {
    return (
      <div>
        {/* Range selector even when empty */}
        <div className="flex justify-end mb-6">
          <div className="inline-flex bg-[var(--surface-2)] border border-[var(--border)] rounded-[9px] p-[3px]">
            {(["7", "30", "90"] as const).map((r) => (
              <button
                key={r}
                onClick={() => handleRangeChange(r)}
                className={
                  range === r
                    ? "px-[13px] py-[5px] text-[12.5px] font-semibold text-[#F4F2E8] bg-[var(--accent)] rounded-md transition-colors"
                    : "px-[13px] py-[5px] text-[12.5px] text-[var(--text-3)] rounded-md hover:text-[var(--text-2)] transition-colors"
                }
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
        <div className="text-center py-16">
          <p className="text-sm text-[var(--text-3)]">
            No queries yet. Queries from the Teams bot and admin chat will
            appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-[18px]">
      {/* Range selector */}
      <div className="flex justify-end">
        <div className="inline-flex bg-[var(--surface-2)] border border-[var(--border)] rounded-[9px] p-[3px]">
          {(["7", "30", "90"] as const).map((r) => (
            <button
              key={r}
              onClick={() => handleRangeChange(r)}
              className={
                range === r
                  ? "px-[13px] py-[5px] text-[12.5px] font-semibold text-[#F4F2E8] bg-[var(--accent)] rounded-md transition-colors"
                  : "px-[13px] py-[5px] text-[12.5px] text-[var(--text-3)] rounded-md hover:text-[var(--text-2)] transition-colors"
              }
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Conversations"
          value={totalQueries}
          numberClass="text-[var(--text)]"
        />
        <StatCard
          label="High Confidence Responses"
          value={stats.confidenceBreakdown.high}
          numberClass="text-[var(--ok-text)]"
          dotClass="bg-[var(--ok-dot)]"
        />
        <StatCard
          label="Unanswered Responses"
          value={stats.insufficientLast30Days}
          numberClass="text-[var(--warn-text)]"
          dotClass="bg-[var(--warn-dot)]"
        />
        <StatCard
          label="Via Teams Bot"
          value={stats.sourceBreakdown.TEAMS_BOT}
          numberClass="text-[var(--accent)]"
        />
      </div>

      {/* Charts */}
      <BarChart data={stats.queriesByDay} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ConfidenceBar breakdown={stats.confidenceBreakdown} />
        <SourceBreakdown breakdown={stats.sourceBreakdown} />
      </div>
    </div>
  );
}
